import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import { DacklFilterSingleSelectItem, DacklFilterSingleSelectComponent, DacklInstantApplySearchComponent } from '@datev/ngx-dackl';
import {Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {Download, isProduct, Product} from '../../model';
import {ProductsDatasource} from '../../shared/misc/products-datasource';
import {DownloadStore} from '../../shared/services/download-store';
import {ProductGroupMapperService} from '../../shared/services/product-group-mapper.service';
import {ProductService} from '../../shared/services/product.service';
import { MatDivider } from '@angular/material/divider';
import { LetDirective, PushPipe } from '@ngrx/component';
import { DownloadsTableComponent } from '../../components/downloads-table/downloads-table.component';

@Component({
    selector: 'lib-products',
    templateUrl: './products.component.html',
    styleUrls: ['./products.component.scss'],
    imports: [
        MatDivider,
        LetDirective,
        DacklFilterSingleSelectComponent,
        DacklInstantApplySearchComponent,
        DownloadsTableComponent,
        PushPipe,
    ],
})
export class ProductsComponent implements OnInit, OnDestroy {
  private pageNumber: number = 0;
  private pageSize: number = 20;

  public productGroups: DacklFilterSingleSelectItem<string>[] =
    this.productGroupMapperService.getProductGroups();
  public productGroupLabel = 'Produktgruppe';

  public allDataLoaded: boolean = false;

  public readonly productGroup =
    this.productGroupMapperService.getProductGroups();

  public readonly productDataSource: ProductsDatasource =
    new ProductsDatasource();

  public selectedDownload$ = this.downloadStore.selectedDownload$.pipe(
    map(selectedDownload =>
      selectedDownload !== undefined && isProduct(selectedDownload)
        ? selectedDownload
        : undefined,
    ),
  );

  public readonly showProductGroupFilter$ =
    this.productDataSource.hasError$.pipe(map(hasError => !hasError));

  public productGroupFilter: string | null = null;
  public productNameFilter: string = '';
  public isFirstProductNameFilterEmission: boolean = true;

  private debounceTimeout: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private readonly productService: ProductService,
    private readonly productGroupMapperService: ProductGroupMapperService,
    private readonly downloadStore: DownloadStore,
    @Inject('Window') private readonly window: Window,
  ) {}

  public ngOnInit(): void {
    this.pageNumber = 0;
    this.productDataSource.replaceDownloads(
      this.getProductStreamFromClassProperties(),
    );
  }

  public ngOnDestroy(): void {
    this.productDataSource.cleanUp();
    this.downloadStore.resetSelectedDownload();
  }

  public filterProductGroup(selectedProductGroup: string): void {
    this.resetOnFilterChange();
    this.productGroupFilter = selectedProductGroup;
    this.productDataSource.replaceDownloads(
      this.getProductStreamFromClassProperties(),
    );
  }

  public filterProductName(selectedProductName: string): void {
    if (this.isFirstProductNameFilterEmission) {
      this.isFirstProductNameFilterEmission = false;
      return;
    }
    this.clearTimeout();

    this.debounceTimeout = setTimeout(() => {
      this.resetOnFilterChange();
      this.productNameFilter = selectedProductName;
      this.productDataSource.replaceDownloads(
        this.getProductStreamFromClassProperties(),
      );
      this.clearTimeout();
    }, 350);
  }

  private clearTimeout(): void {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
  }

  private resetOnFilterChange(): void {
    this.downloadStore.resetSelectedDownload();
    this.pageNumber = 0;
    this.allDataLoaded = false;
  }

  private loadData(): void {
    this.productDataSource.addDownloads(
      this.getProductStreamFromClassProperties(),
    );
  }

  private getProductStreamFromClassProperties(): Observable<Array<Product>> {
    const filter = {
      ...(this.productGroupFilter !== '' &&
        this.productGroupFilter !== null && {
          productGroup: this.productGroupFilter,
        }),
      ...(this.productNameFilter !== '' && {
        name: this.productNameFilter,
      }),
    };

    return this.productService
      .getProducts(
        Object.keys(filter).length === 0 ? null : filter,
        this.pageNumber,
        this.pageSize,
      )
      .pipe(
        tap(productPage => {
          this.allDataLoaded = productPage.last;
        }),
        map(productPage => productPage.content),
      );
  }

  public loadMoreData(): void {
    if (!this.allDataLoaded) {
      this.pageNumber++;
      this.loadData();
    }
  }

  public getProductGroupLabel(productGroupKey: string): string {
    return this.productGroupMapperService.getProductGroupLabel(
      productGroupKey,
      'Produktgruppe',
    );
  }

  public handleTableRowSelection(download: Download): void {
    this.downloadStore.setSelectedDownload(download);
  }

  public reloadSite(): void {
    this.window.location.reload();
  }
}
